import { Todo, UserProgress, CompletionTiming } from '@/types/todo';
import { loadTimeSettings } from './timeSettings';
import { resetDailyPaywallState } from './paywallHelpers';

// Level progression requirements (20 levels)
export const LEVEL_REQUIREMENTS = [
  0, 10, 22, 36, 52, 70, 90, 112, 136, 162,
  190, 220, 252, 286, 322, 360, 400, 442, 486, 532, 580
];

export const MAX_LEVEL = 20;
export const MAX_COMPLETED_TODOS = 99;

// Get the reset day (considering configurable reset hour cutoff)
export function getResetDay(): string {
  const now = new Date();
  const { resetHour } = loadTimeSettings();
  
  // If it's before reset hour, consider it the previous day for reset purposes
  if (now.getHours() < resetHour) {
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    return yesterday.toDateString();
  }
  
  return now.toDateString();
}

// Create deadline for configurable end of day time
export function createDeadline(): Date {
  const now = new Date();
  const { endOfDayHour } = loadTimeSettings();
  
  const deadline = new Date();
  deadline.setHours(endOfDayHour, 0, 0, 0);
  
  // If current time is past end-of-day, set deadline to next day
  if (now.getHours() >= endOfDayHour) {
    deadline.setDate(deadline.getDate() + 1);
  }
  
  return deadline;
}

// Create deadline based on duration in minutes
export function createDeadlineFromDuration(durationMinutes: number): Date {
  const now = new Date();
  return new Date(now.getTime() + durationMinutes * 60 * 1000);
}

// Calculate completion timing and points
export function calculateCompletion(createdAt: Date, deadline: Date, isOverdrive: boolean): CompletionTiming & { points: number } {
  const now = new Date();
  const totalTime = deadline.getTime() - createdAt.getTime();
  const elapsedTime = now.getTime() - createdAt.getTime();
  const progressPercent = Math.min(Math.max((elapsedTime / totalTime) * 100, 0), 100);
  
  // Determine completion category based on progress percentage
  let basePoints: number;
  let isEarly = false;
  
  if (progressPercent <= 25) {
    // Early completion (0-25% progress)
    basePoints = 14;
    isEarly = true;
  } else if (progressPercent <= 50) {
    // On time (25-50% progress)
    basePoints = 13;
  } else if (progressPercent <= 75) {
    // Late (50-75% progress)
    basePoints = 12;
  } else if (progressPercent <= 100) {
    // Very late (75-100% progress)
    basePoints = 11;
  } else {
    // Overdue (>100% progress)
    basePoints = 10;
  }
  
  // Triple points for overdrive tasks
  const points = isOverdrive ? basePoints * 3 : basePoints;
  
  return { isEarly, progressPercent, points };
}

// Calculate level from total points
export function calculateLevel(totalPoints: number): { level: number; pointsToNext: number; progressPercent: number } {
  let level = 1;
  
  // Find current level
  for (let i = LEVEL_REQUIREMENTS.length - 1; i >= 0; i--) {
    if (totalPoints >= LEVEL_REQUIREMENTS[i]) {
      level = i + 1;
      break;
    }
  }
  
  // Cap at max level
  if (level > MAX_LEVEL) {
    return { level: MAX_LEVEL, pointsToNext: 0, progressPercent: 100 };
  }
  
  const pointsToNext = LEVEL_REQUIREMENTS[level] - totalPoints;
  const levelStart = level > 1 ? LEVEL_REQUIREMENTS[level - 1] : 0;
  const levelEnd = LEVEL_REQUIREMENTS[level];
  const progressPercent = ((totalPoints - levelStart) / (levelEnd - levelStart)) * 100;
  
  return { level, pointsToNext, progressPercent };
}

// Get time remaining as string
export function getTimeRemaining(deadline: Date): string {
  const now = new Date();
  const diff = deadline.getTime() - now.getTime();
  
  if (diff <= 0) return 'Overdue';
  
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((diff % (1000 * 60)) / 1000);
  
  if (hours > 0) {
    return `${hours}h ${minutes}m ${seconds}s`;
  }
  return `${minutes}m ${seconds}s`;
}

// Get progress percentage for countdown bar
export function getProgressPercent(createdAt: Date, deadline: Date): number {
  const now = new Date();
  const totalTime = deadline.getTime() - createdAt.getTime();
  const elapsedTime = now.getTime() - createdAt.getTime();
  return Math.min(Math.max((elapsedTime / totalTime) * 100, 0), 100);
}

// Trim completed todos to keep only the most recent ones
export function trimCompletedTodos(todos: Todo[]): Todo[] {
  if (todos.length <= MAX_COMPLETED_TODOS) {
    return todos;
  }
  
  // Sort by completedAt date (newest first), handling undefined completedAt
  const sorted = todos.sort((a, b) => {
    const aTime = a.completedAt?.getTime() || 0;
    const bTime = b.completedAt?.getTime() || 0;
    return bTime - aTime;
  });
  
  // Return only the most recent MAX_COMPLETED_TODOS
  return sorted.slice(0, MAX_COMPLETED_TODOS);
}

// Convert overdue tasks to overdrive (now only used by paywall)
export function convertToOverdrive(todos: Todo[]): { todos: Todo[]; conversionCount: number } {
  // No automatic conversion - only paywall converts to overdrive
  return { todos, conversionCount: 0 };
}

// Daily reset with paywall state reset
export function performDailyReset(todos: Todo[], userProgress: UserProgress): {
  todos: Todo[];
  userProgress: UserProgress;
  conversionCount: number;
} {
  // No automatic overdrive conversion - overdue tasks stay overdue until paid
  const newProgress: UserProgress = {
    totalPoints: 0,
    currentLevel: 1,
    lastResetDate: getResetDay()
  };
  
  // Reset paywall state as well
  resetDailyPaywallState();
  
  return {
    todos,
    userProgress: newProgress,
    conversionCount: 0
  };
}

// Storage helpers
export function saveTodos(key: string, todos: Todo[]): void {
  const todosToSave = key === 'completedTodos' ? trimCompletedTodos(todos) : todos;
  localStorage.setItem(key, JSON.stringify(todosToSave));
}

export function loadTodos(key: string): Todo[] {
  const stored = localStorage.getItem(key);
  if (!stored) return [];
  
  return JSON.parse(stored).map((todo: any) => ({
    ...todo,
    createdAt: new Date(todo.createdAt),
    deadline: new Date(todo.deadline),
    completedAt: todo.completedAt ? new Date(todo.completedAt) : undefined
  }));
}

export function saveProgress(progress: UserProgress): void {
  localStorage.setItem('userProgress', JSON.stringify(progress));
}

export function loadProgress(): UserProgress {
  const stored = localStorage.getItem('userProgress');
  if (!stored) {
    return {
      totalPoints: 0,
      currentLevel: 1,
      lastResetDate: getResetDay()
    };
  }
  
  return JSON.parse(stored);
}